import React, { useState } from "react";
import { FaUser, FaLock } from "react-icons/fa";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { Link, useNavigate } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import Breadcrump from "../../components/Breadcrump";
import { ArrowRight, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const Register = () => {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (!isValidPassword) {
      newErrors.confirmPassword =
        "Confirm Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.general = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <>
      <Breadcrump title=" Register" />

      <div className="portal-wrapper">
        <div className="portal-container">
          <div className="portal-image-arch">
            <img src={`${process.env.PUBLIC_URL}/images/login-register.png`} alt="Ritual" className="portal-img" />
            {/* Gradient overlay for text readability */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(44, 58, 49, 0.7), transparent)",
              }}
            ></div>

            <div className="portal-quote">
              <Sparkles
                className="mb-2 text-white opacity-75"
                size={24}
                style={{ margin: "0 auto" }}
              />
              <p className="quote-text">"Where science meets the sacred."</p>
            </div>
          </div>

          <div className="portal-form-card">
            <AnimatePresence mode="wait">
              {/* --- LOGIN FORM --- */}

              <motion.div
                key="login"
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                 <h1 className="form-headline">Sign Up</h1>
                
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="portal-input-group col-md-6">
                      <input
                        type="text"
                        className="portal-input"
                        name="firstName"
                        placeholder=""
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                      <label className="portal-label ms-2  px-2">
                        First Name <span className="text-danger">*</span>
                      </label>
                      {errors && errors.firstName && (
                        <span className="text-danger">{errors.firstName}</span>
                      )}
                    </div>
                    <div className="portal-input-group col-md-6">
                      <input
                        className="portal-input"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder=""
                      />
                      <label className="portal-label ms-2  px-2">Last Name</label>
                    </div>
                  </div>
                  <div className="portal-input-group ">
                    <input
                      type="email"
                      className="portal-input"
                      name="email"
                      placeholder=""
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <label className="portal-label  px-2">Email Address</label>
                    {errors && errors.email && (
                      <span className="text-danger">{errors.email}</span>
                    )}
                  </div>

                  <div className="portal-input-group">
                    <input
                      type="password"
                      className="portal-input"
                      name="password"
                      placeholder=""
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <label className="portal-label px-2">
                      Password <span className="text-danger">*</span>
                    </label>
                    {errors && errors.password && (
                      <span className="text-danger">{errors.password}</span>
                    )}
                  </div>
                  <div className="portal-input-group">
                    <input
                      type="password"
                      className="portal-input"
                      name="confirmPassword"
                      placeholder=""
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    <label className="portal-label px-2">
                      {" "}
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    {errors && errors.confirmPassword && (
                      <span className="text-danger">
                        {errors.confirmPassword}
                      </span>
                    )}
                  </div>

                  <button className="btn-portal" type="submit">
                    Register <ArrowRight size={16} className="ms-2" />
                  </button>
                  <div className="d-flex justify-content-between align-items-center mb-5 mt-2">
                    <label
                      className="small"
                      style={{
                        color: "var(--portal-text-soft)",
                        cursor: "pointer",
                        fontFamily: "var(--font-body)",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="me-2"
                        style={{ accentColor: "var(--portal-accent)" }}
                      />{" "}
                      Have an account
                    </label>
                    <Link
                      to="/users/login   "
                      className="small text-decoration-none"
                      style={{
                        color: "var(--portal-text-deep)",
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      Sign in
                    </Link>
                  </div>
                </form>
              </motion.div>

              {/* --- REGISTER FORM --- */}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
